import {Download} from 'lucide-react';
import {Button} from '@/components/ui/button';
import type {ImmutableExerciseReport} from '@/lib/api/v2';

type ScoreMap = Map<string, number>;

interface ParticipantsTemplateExportButtonProps {
    data: ImmutableExerciseReport | null;
    archiveUuid?: string | null;
    teamIdToName: Map<number, string>;
    participantTtxScoreMap: ScoreMap;
    participantTtxOutOfMap: ScoreMap;
    participantScenarioScoreMap: ScoreMap;
    participantScenarioOutOfMap: ScoreMap;
}

function getParticipantTeamId(p: {
    team: { id: number } | null | undefined;
}): number {
    return Number(p.team?.id ?? 0);
}

export default function ParticipantsTemplateExportButton(props: ParticipantsTemplateExportButtonProps) {
    const {
        data,
        archiveUuid,
        teamIdToName,
        participantTtxScoreMap,
        participantTtxOutOfMap,
        participantScenarioScoreMap,
        participantScenarioOutOfMap,
    } = props;

    const exportParticipantsToExcel = async () => {
        if (!data) return;

        const phaseFromScenario = Array.isArray((data as any)?.scenario?.phases)
            ? ((data as any).scenario.phases as Array<{ phase_name?: string; order?: number }>)
                  .slice()
                  .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
                  .map((p) => String(p?.phase_name || '').trim())
                  .filter(Boolean)
            : [];

        const normalizePhase = (name?: string): string => {
            const n = String(name || '').trim();
            if (!n) return 'Unassigned';
            return n;
        };

        const phaseOrder = (all: string[], preferred: string[]): string[] => {
            const uniq = Array.from(new Set(all.map(normalizePhase)));
            const preferredNorm = preferred.map(normalizePhase);
            const known = preferredNorm.filter((p) => uniq.includes(p));
            const extra = uniq
                .filter((p) => !preferredNorm.includes(p))
                .sort((a, b) => a.localeCompare(b));
            return [...known, ...extra];
        };

        const ttxPhases = phaseOrder(
            data.participants.flatMap((p) => (p.score?.ttx?.phase ?? []).map((ph) => ph?.name || '')),
            phaseFromScenario,
        );
        const scenarioPhases = phaseOrder(
            data.participants.flatMap((p) => (p.score?.scenarios?.phase ?? []).map((ph) => ph?.name || '')),
            phaseFromScenario,
        );

        // Always include all scenario phases from API in export columns.
        // If a participant has no score/flags on a phase, row shows 0 / 0.
        const canonicalPhases = phaseFromScenario.length ? phaseFromScenario : phaseOrder(
            [...scenarioPhases, ...ttxPhases],
            [],
        );
        const objPhases = canonicalPhases.length ? canonicalPhases : ['Unassigned'];
        const subjPhases = canonicalPhases.length ? canonicalPhases : ['Unassigned'];
        const totalPhases = canonicalPhases.length ? canonicalPhases : ['Unassigned'];

        const header1: Array<string | null> = ['Participant', null, null, 'Objective'];
        for (let i = 1; i < objPhases.length * 2; i += 1) header1.push(null);
        header1.push('Subjective');
        for (let i = 1; i < subjPhases.length * 2; i += 1) header1.push(null);
        header1.push('Objective + Subjective');
        for (let i = 1; i < totalPhases.length * 2; i += 1) header1.push(null);
        header1.push('Grand Total', null, null);

        const header2: Array<string | null> = [null, null, null];
        for (const ph of objPhases) header2.push(ph, null);
        for (const ph of subjPhases) header2.push(ph, null);
        for (const ph of totalPhases) header2.push(ph, null);
        header2.push(null, null, null);

        const header3: Array<string | null> = ['ID', 'Name', 'Team'];
        for (let i = 0; i < objPhases.length + subjPhases.length + totalPhases.length; i += 1) {
            header3.push('Obtained', 'Out of');
        }
        header3.push('Obtained', 'Out of', 'Per.%');

        const aoa: Array<Array<string | number | null>> = [header1, header2, header3];

        for (const p of data.participants) {
            const key = `${p.user_id}:${getParticipantTeamId(p)}`;
            const ttxPhaseMap = new Map<string, { obtained: number; outOf: number }>();
            const scenarioPhaseMap = new Map<string, { obtained: number; outOf: number }>();

            for (const ph of p.score?.ttx?.phase ?? []) {
                const name = normalizePhase(ph?.name);
                const obtained = Number(ph?.obtained_score ?? 0) + Number(ph?.bonus_points ?? 0);
                const outOf = Number(ph?.out_of ?? 0);
                ttxPhaseMap.set(name, {obtained, outOf});
            }
            for (const ph of p.score?.scenarios?.phase ?? []) {
                const name = normalizePhase(ph?.name);
                const obtained = Number(ph?.obtained_score ?? 0) + Number(ph?.bonus_points ?? 0);
                const outOf = Number(ph?.out_of ?? 0);
                scenarioPhaseMap.set(name, {obtained, outOf});
            }

            if (ttxPhaseMap.size === 0) {
                const obtained = participantTtxScoreMap.get(key) ?? 0;
                const outOf = participantTtxOutOfMap.get(key) ?? 0;
                if (obtained || outOf) ttxPhaseMap.set('Unassigned', {obtained, outOf});
            }
            if (scenarioPhaseMap.size === 0) {
                const obtained = participantScenarioScoreMap.get(key) ?? 0;
                const outOf = participantScenarioOutOfMap.get(key) ?? 0;
                if (obtained || outOf) scenarioPhaseMap.set('Unassigned', {obtained, outOf});
            }

            const row: Array<string | number | null> = [
                p.user_id,
                p.full_name || `User ${p.user_id}`,
                teamIdToName.get(getParticipantTeamId(p)) || p.team?.name || '—',
            ];
            for (const ph of objPhases) {
                const val = scenarioPhaseMap.get(ph) || {obtained: 0, outOf: 0};
                row.push(val.obtained, val.outOf);
            }
            for (const ph of subjPhases) {
                const val = ttxPhaseMap.get(ph) || {obtained: 0, outOf: 0};
                row.push(val.obtained, val.outOf);
            }
            for (const ph of totalPhases) {
                const sc = scenarioPhaseMap.get(ph) || {obtained: 0, outOf: 0};
                const ttx = ttxPhaseMap.get(ph) || {obtained: 0, outOf: 0};
                row.push(sc.obtained + ttx.obtained, sc.outOf + ttx.outOf);
            }
            const objectiveTotalObtained = objPhases.reduce((acc, ph) => acc + (scenarioPhaseMap.get(ph)?.obtained || 0), 0);
            const objectiveTotalOutOf = objPhases.reduce((acc, ph) => acc + (scenarioPhaseMap.get(ph)?.outOf || 0), 0);
            const subjectiveTotalObtained = subjPhases.reduce((acc, ph) => acc + (ttxPhaseMap.get(ph)?.obtained || 0), 0);
            const subjectiveTotalOutOf = subjPhases.reduce((acc, ph) => acc + (ttxPhaseMap.get(ph)?.outOf || 0), 0);
            const grandObtained = objectiveTotalObtained + subjectiveTotalObtained;
            const grandOutOf = objectiveTotalOutOf + subjectiveTotalOutOf;
            row.push(grandObtained, grandOutOf, grandOutOf > 0 ? Number(((grandObtained / grandOutOf) * 100).toFixed(2)) : 0);
            aoa.push(row);
        }

        const objectiveStart = 3;
        const objectiveEnd = objectiveStart + objPhases.length * 2 - 1;
        const subjectiveStart = objectiveEnd + 1;
        const subjectiveEnd = subjectiveStart + subjPhases.length * 2 - 1;
        const totalStart = subjectiveEnd + 1;
        const totalEnd = totalStart + totalPhases.length * 2 - 1;
        const grandStart = totalEnd + 1;
        const grandEnd = grandStart + 2;

        const {Workbook} = await import('exceljs');
        const wb = new Workbook();
        const ws = wb.addWorksheet('Overall', {views: [{state: 'frozen', xSplit: 3, ySplit: 3}]});

        aoa.forEach((row, rIdx) => row.forEach((val, cIdx) => {
            ws.getCell(rIdx + 1, cIdx + 1).value = val as any;
        }));

        ws.mergeCells(1, 1, 2, 3);
        ws.mergeCells(1, objectiveStart + 1, 1, objectiveEnd + 1);
        ws.mergeCells(1, subjectiveStart + 1, 1, subjectiveEnd + 1);
        ws.mergeCells(1, totalStart + 1, 1, totalEnd + 1);
        ws.mergeCells(1, grandStart + 1, 2, grandEnd + 1);
        objPhases.forEach((_, i) => ws.mergeCells(2, objectiveStart + 1 + i * 2, 2, objectiveStart + 2 + i * 2));
        subjPhases.forEach((_, i) => ws.mergeCells(2, subjectiveStart + 1 + i * 2, 2, subjectiveStart + 2 + i * 2));
        totalPhases.forEach((_, i) => ws.mergeCells(2, totalStart + 1 + i * 2, 2, totalStart + 2 + i * 2));

        const widths = [2, 28, 28, ...Array.from({length: objPhases.length * 2 + subjPhases.length * 2 + totalPhases.length * 2 + 3}, () => 11)];
        widths.forEach((w, i) => {
            ws.getColumn(i + 1).width = w;
        });

        const colorize = (r: number, cStart: number, cEnd: number, argb: string) => {
            for (let c = cStart; c <= cEnd; c += 1) {
                const cell = ws.getCell(r + 1, c + 1);
                cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb}};
                cell.font = {bold: true, color: {argb: 'FF111827'}};
                cell.alignment = {horizontal: 'center', vertical: 'middle'};
                cell.border = {
                    top: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                    left: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                    bottom: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                    right: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                };
            }
        };
        colorize(0, 0, 2, 'FFE5E7EB');
        colorize(1, 0, 2, 'FFF3F4F6');
        colorize(2, 0, 2, 'FFF9FAFB');
        colorize(0, objectiveStart, objectiveEnd, 'FFDDEBF7');
        colorize(1, objectiveStart, objectiveEnd, 'FFEAF3FB');
        colorize(2, objectiveStart, objectiveEnd, 'FFF2F8FD');
        colorize(0, subjectiveStart, subjectiveEnd, 'FFFCE4D6');
        colorize(1, subjectiveStart, subjectiveEnd, 'FFFDF0E8');
        colorize(2, subjectiveStart, subjectiveEnd, 'FFFEF6EF');
        colorize(0, totalStart, totalEnd, 'FFE2F0D9');
        colorize(1, totalStart, totalEnd, 'FFECF6E8');
        colorize(2, totalStart, totalEnd, 'FFF3FAF0');
        colorize(0, grandStart, grandEnd, 'FFFFF2CC');
        colorize(1, grandStart, grandEnd, 'FFFFF8DE');
        colorize(2, grandStart, grandEnd, 'FFFFFBEB');

        const fileBase = archiveUuid || 'exercise-archive';
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `exercise-archive-${fileBase}-overall-template.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href);
    };

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={() => void exportParticipantsToExcel()}
            disabled={!data?.participants?.length}
        >
            <Download className="h-4 w-4 mr-2"/>
            Export Excel
        </Button>
    );
}

